import { getActionError } from "../../utils/actionFeedback";
import { toast } from "react-toastify";
import React, { useCallback, useContext, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { CiSquareQuestion } from "react-icons/ci";
import RequestSessionModal from "../../Components/RequestSessionModal";

import { DarkModeContext } from "../../Context/DarkModeContext";
import axiosInstance from "../../Context/axiosInstance";
import { visibleItems } from "../../utils/visibility";
import SessionModal from "../../Components/SessionModal.jsx";
import { ImCheckmark } from "react-icons/im";

function StudentLiveRequest() {
  const { isDarkMode } = useContext(DarkModeContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState("StartDate");
  const [sortDirection, setSortDirection] = useState("desc");

  const [requestOpen, setRequestOpen] = useState(false);
  const latestRequest = useRef(0);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState([]); // Ensure this is initialized as an array
  const [totalPages, setTotalPages] = useState(1);

  // RESERVE A SESSION
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [alert, setAlert] = useState({ message: "", type: "" }); // New alert state

  const handleRowClick = (session) => {
    setSelectedSession(session);
    setModalOpen(true);
  };

  // GET Request: Fetching Sessions
  const fetchSessions = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    setError([]); // Reset errors

    try {
      const response = await axiosInstance.get(
        `/sessions?page=${page}&pageSize=${pageSize}&sortColumn=${sortColumn}&sortDirection=${sortDirection}&search=${encodeURIComponent(
          searchQuery
        )}`
      );

      if (requestId !== latestRequest.current) return;
      setSessions(visibleItems(response.data.response || []));
      setTotalPages(Math.max(1, Math.ceil(response.data.totalCount / pageSize)));
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      if (error.response && error.response.status === 401) {
        // Handle unauthorized error (token expired or invalid)
      } else {
        setError([error.message || "An unexpected error occurred."]);
      }
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [page, pageSize, searchQuery, sortColumn, sortDirection]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleReserve = async (whatsappNumber) => {
    if (!selectedSession) return;

    try {
      const response = await axiosInstance.post(
        `/sessions/${selectedSession.id}/reserve`,
        { whatsappNumber }
      );
      toast.success("Session reserved successfully.");
      if (response.status === 200) {
        fetchSessions();
        setAlert({
          message: "Session reserved successfully!",
          type: "success",
        });
        setTimeout(() => setAlert({ message: "", type: "" }), 3000);
      }
    } catch (error) {
      toast.error(getActionError(error, "Could not reserve the session."));
      if (error.response) {
        // Extract validation errors if present
        const apiErrors = error.response.data.errors || [];
        const errorMessages = apiErrors.map(
          (err) => `${err.name}: ${err.reason}`
        );
        setError(
          errorMessages.length > 0
            ? errorMessages
            : ["An unexpected error occurred."]
        );
      } else {
        setError(["An unexpected error occurred."]);
      }
    } finally {
      setModalOpen(false);
      setSelectedSession(null);
    }
  };

  const goToNextPage = () => {
    navigate(+1);
  };

  const goToPreviousPage = () => {
    navigate(-1);
  };

  const formattedPath = `${location.pathname
    .slice(1)
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\//g, " > ")}`;

  const handlePageChange = (newPage) => {
    setPage(newPage);
    navigate(`?page=${newPage}&pageSize=${pageSize}`);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setPage(1);
    navigate(`?page=1&pageSize=${newSize}`);
  };

  const toggleSortDirection = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  return (
    <div
      className={`flex flex-col lg:flex-row lg:items-start ${
        isDarkMode ? "bg-[#181818] text-black" : "bg-customBlue text-black"
      } h-dvh min-h-[600px] p-4 pt-24 lg:p-8 lg:pt-24 flex-1`}
    >
      <div
        className={`${
          isDarkMode ? "bg-[#1e1e1e] text-white" : "bg-white text-black"
        } flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-4 lg:p-8 rounded-lg shadow-xl`}
      >
        {/* Alert Message */}
        {alert.message && (
          <div
            role="status"
            className={`mb-4 shrink-0 rounded-xl border px-4 py-3 text-sm ${
              alert.type === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" : "border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
            }`}
          >
            {alert.message}
          </div>
        )}
        {/* PATH AND CONTROLS */}
        <div className="flex shrink-0 flex-wrap w-full justify-between items-center gap-2 mb-4 text-gray-700 text-sm capitalize">
          <div className="flex min-w-0 flex-wrap gap-1 items-center">
            <button
              onClick={goToPreviousPage}
              className="w-[24px] h-[20px] flex justify-center items-center bg-gray-300 rounded-[6px] disabled:opacity-50"
            >
              <IoIosArrowBack />
            </button>
            <button
              onClick={goToNextPage}
              className="w-[24px] h-[20px] flex justify-center items-center bg-gray-300 rounded-[6px] disabled:opacity-50"
            >
              <IoIosArrowForward />
            </button>
            <div
              className={`${
                isDarkMode ? "text-white" : "text-black"
              } min-w-0 break-words text-sm capitalize px-2 py-4`}
            >
              <span>{formattedPath}</span>
            </div>
          </div>

          <div className="min-w-0 flex-1 sm:max-w-[320px]">
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              aria-label="Search sessions"
              className={`${
                isDarkMode
                  ? "bg-[#292929] text-white"
                  : "bg-gray-100 text-black"
              } px-4 py-2 border border-gray-300 rounded-lg w-full`}
            />
          </div>

          <div className="flex flex-col lg:flex-row lg:gap-4">
            <button
              onClick={() => setRequestOpen(true)}
              className="flex lg:hover:scale-110 transition-transform ease-in-out duration-300 items-center mb-2 lg:mb-0 px-4 py-2 lg:px-6 lg:py-3 justify-center bg-red-500 text-white rounded-lg"
            >
              <CiSquareQuestion size={30} className="pr-2" />
              Request A Date
            </button>
          </div>
        </div>

        <div className="relative min-h-0 flex-1 overflow-auto rounded-xl border border-gray-200 bg-inherit min-w-0 max-w-full" aria-busy={loading}>
          <table className="min-w-full">
            <thead
              className={`sticky top-0 z-10 ${
                isDarkMode
                  ? "bg-[#1e1e1e]  text-gray-300"
                  : "bg-white text-black"
              }`}
            >
              <tr>
                <th
                  onClick={() => toggleSortDirection("Name")}
                  className="cursor-pointer px-4 py-2 text-center text-base font-medium uppercase tracking-wider"
                >
                  Name
                  {sortColumn === "Name" && (
                    <span className="text-sm px-1 items-center text-blue-400">
                      {sortDirection === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => toggleSortDirection("StartDate")}
                  className="cursor-pointer px-4 py-2 text-center text-base font-medium uppercase tracking-wider"
                >
                  Start Date
                  {sortColumn === "StartDate" && (
                    <span className="text-sm px-1 items-center text-blue-400">
                      {sortDirection === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th className="px-4 py-2 text-center text-base font-medium uppercase tracking-wider">
                  Reserved
                </th>
              </tr>
            </thead>
            <tbody>
              {!loading && sessions.length > 0 &&
                sessions.map((session) => (
                  <tr
                    key={session.id}
                    className={`transition-all duration-200 ${
                      isDarkMode
                        ? "hover:bg-gray-800 rounded-[10px]"
                        : "hover:bg-gray-200 rounded-[10px]"
                    }`}
                  >
                    <td className="px-4 py-2 whitespace-nowrap text-base text-center">
                      {session.name}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-base text-center text-[#2083D8]">
                      {new Date(session.startDate).toLocaleString("en-EG", {
                        timeZone: "Africa/Cairo",
                      })}
                    </td>
                    <td
                      className={`flex justify-center items-center h-16 px-4 py-2 whitespace-nowrap text-base`}
                    >
                      {session.isReserved === false ? (
                        <button
                          onClick={() => handleRowClick(session)}
                          type="button"
                          className={`${
                            isDarkMode
                              ? "border-2 border-customRed hover:bg-customRed hover:scale-105 ease-in-out duration-300 text-white"
                              : "bg-sky-700 hover:bg-sky-500 hover:scale-105 ease-in-out duration-300 text-white"
                          } px-4 py-2 rounded-[5px]`}
                        >
                          Reserve
                        </button>
                      ) : (
                        <ImCheckmark
                          size={20}
                          className={`${
                            isDarkMode ? "text-green-400" : "text-green-600"
                          }`}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              {!sessions.length && !loading && (
                <tr className="h-64">
                  <td
                    colSpan="3"
                    className={`${
                      isDarkMode ? "text-white" : "text-gray-500"
                    } text-center text-lg text-gray-500`}
                  >
                    No Sessions found
                  </td>
                </tr>
              )}
              {loading && (
                <tr className="h-64">
                  <td
                    colSpan="3"
                    className={`${
                      isDarkMode ? "text-white" : "text-gray-500"
                    } px-2 py-5 text-center text-lg`}
                  >
                    <div className="flex justify-center items-center">
                      <div role="status" aria-label="Loading sessions" className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-300">
                        <span aria-hidden="true" className="h-7 w-7 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none" />
                        Loading sessions...
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <SessionModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={handleReserve}
          sessionDetails={selectedSession}
          errors={error}
        />

        <RequestSessionModal open={requestOpen} onClose={() => setRequestOpen(false)} isDarkMode={isDarkMode} />
        {totalPages > 0 && (
          <div
            className="flex shrink-0 items-center justify-end pt-4 text-black"
          >
            <label
              htmlFor="pageSize"
              className={`${
                isDarkMode ? "text-white" : "text-gray-700"
              } mr-2 text-sm`}
            >
              Sessions per page:
            </label>
            <select
              id="pageSize"
              disabled={loading}
              value={pageSize}
              onChange={handlePageSizeChange}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            >
              {[20, 30, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}
        {/* Pagination Controls (Bottom) */}
        {totalPages > 0 && (
          <div
            className="flex shrink-0 justify-center pt-3 text-white"
          >
            <div className="max-h-24 overflow-y-auto">
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index + 1}
                  disabled={loading}
                  onClick={() => handlePageChange(index + 1)}
                  className={`px-4 py-2 ${
                    page === index + 1
                      ? isDarkMode
                        ? "bg-customRed"
                        : "bg-customBlue"
                      : isDarkMode
                      ? "bg-gray-300 text-black"
                      : "bg-gray-300"
                  } rounded-[5px] mx-1 my-1`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentLiveRequest;
